import React, { useState, useEffect, useContext, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  IconButton,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { AlertMessagesContext } from 'react-alert-messages';
import DealerServices from '../../services/DealerServices';
import { PAYMENT_MODES } from '../../const';

const INITIAL_FORM = {
  dealerId: '',
  amount: '',
  paymentMode: 'CASH',
  referenceNo: '',
  notes: '',
};

function extractDealerArray(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.results)) return res.results;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  if (Array.isArray(res?.data?.results)) return res.data.results;
  return [];
}

const getDealerId = (d) => d?.id ?? d?.dealer_id;
const getDealerName = (d) => d?.name || d?.dealer_name || (d ? `Dealer #${getDealerId(d)}` : '');
const getDealerBalance = (d) =>
  parseFloat(d?.credit_balance ?? d?.wallet_balance ?? d?.balance ?? d?.current_balance ?? 0);

export default function WalletRechargePopup({ open, onClose, dealers, onSuccess, initialDealerId = '' }) {
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [formData, setFormData] = useState({ ...INITIAL_FORM, dealerId: initialDealerId || '' });
  const [dealerList, setDealerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingDealers, setFetchingDealers] = useState(false);

  const handleChange = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!open) {
      setFetchingDealers(false);
      return;
    }

    setFormData({ ...INITIAL_FORM, dealerId: initialDealerId || '' });

    if (Array.isArray(dealers) && dealers.length > 0) {
      setDealerList(dealers);
      setFetchingDealers(false);
    } else {
      fetchDealers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialDealerId]);

  const fetchDealers = async () => {
    setFetchingDealers(true);
    try {
      const res = await DealerServices.getDealers();
      setDealerList(extractDealerArray(res));
    } catch (error) {
      console.error('Failed to load dealers:', error);
      if (postAlertMessage) {
        postAlertMessage({
          text: error?.message || 'Failed to load dealers',
          type: 'failed',
        });
      }
    } finally {
      setFetchingDealers(false);
    }
  };

  const { dealerId, amount, paymentMode, referenceNo, notes } = formData;

  const selectedDealerObj = useMemo(() => {
    if (!dealerId) return null;
    return dealerList.find((d) => String(getDealerId(d)) === String(dealerId)) || null;
  }, [dealerList, dealerId]);

  const currentBal = useMemo(() => {
    return selectedDealerObj ? getDealerBalance(selectedDealerObj) : 0;
  }, [selectedDealerObj]);

  const rechargeAmt = parseFloat(amount) || 0;
  const updatedNewBal = currentBal + rechargeAmt;

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!dealerId) {
      if (postAlertMessage) postAlertMessage({ text: 'Please select a dealer', type: 'failed' });
      return;
    }

    const numericAmount = parseFloat(amount);
    if (isNaN(numericAmount) || numericAmount === 0) {
      if (postAlertMessage) postAlertMessage({ text: 'Please enter a valid payment amount', type: 'failed' });
      return;
    }

    setLoading(true);
    try {
      const dealerName = getDealerName(selectedDealerObj) || `Dealer #${dealerId}`;

      const payload = {
        dealer_id: Number(dealerId),
        dealer_name: dealerName,
        amount: numericAmount,
        payment_mode: paymentMode,
        reference_no: referenceNo,
        notes: notes,
        current_balance: currentBal,
      };

      const res = await DealerServices.rechargeDealerWallet(payload);

      // Update dealer credit_balance in local state array
      const updatedList = dealerList.map((d) => {
        if (String(getDealerId(d)) === String(dealerId)) {
          const newBal = res?.new_balance !== undefined ? res.new_balance : currentBal + numericAmount;
          return { ...d, credit_balance: newBal, balance: newBal, wallet_balance: newBal };
        }
        return d;
      });
      setDealerList(updatedList);

      if (postAlertMessage) {
        postAlertMessage({
          text: res?.message || (numericAmount < 0 ? 'Wallet adjusted successfully!' : 'Wallet recharged successfully!'),
          type: 'success',
        });
      }

      if (onSuccess) {
        onSuccess(res);
      }
      onClose();
    } catch (error) {
      if (postAlertMessage) {
        postAlertMessage({
          text: error?.message || 'Failed to recharge dealer wallet',
          type: 'failed',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        style: {
          borderRadius: 16,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        style={{
          background: 'linear-gradient(135deg, #00a65a 0%, #008d4c 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 24px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 18, fontWeight: 700 }}>
          <AccountBalanceWalletIcon />
          <span>Dealer Wallet Recharge</span>
        </div>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={loading}
          style={{ color: '#ffffff', padding: 4 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers style={{ padding: 24, backgroundColor: '#f9fafb' }}>
          <Grid container spacing={2}>
            {/* Dealer Selection */}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Select Dealer"
                value={dealerId}
                onChange={handleChange('dealerId')}
                variant="outlined"
                required
                disabled={loading || fetchingDealers}
                helperText={fetchingDealers ? 'Loading dealers...' : ''}
              >
                <MenuItem value="" disabled>
                  -- Select Dealer --
                </MenuItem>
                {dealerList.map((dealer, idx) => {
                  const idVal = getDealerId(dealer) ?? idx;
                  const nameVal = getDealerName(dealer);
                  const balVal = getDealerBalance(dealer);
                  const phoneVal = dealer?.mobile_no ? ` (${dealer.mobile_no})` : '';
                  const formattedBal = `${balVal < 0 ? '-' : ''}₹${Math.abs(balVal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
                  return (
                    <MenuItem key={idVal} value={idVal}>
                      {nameVal} &nbsp;—&nbsp; [Bal: {formattedBal}]{phoneVal}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Grid>

            {/* Selected Dealer Balance Details Card */}
            {selectedDealerObj && (
              <Grid item xs={12}>
                <div
                  style={{
                    padding: '12px 16px',
                    backgroundColor: updatedNewBal < 0 ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${updatedNewBal < 0 ? '#fecaca' : '#bbf7d0'}`,
                    borderRadius: 8,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div style={{ fontSize: 12, color: currentBal < 0 ? '#b91c1c' : '#15803d', fontWeight: 600 }}>Current Dealer Balance</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: currentBal < 0 ? '#b91c1c' : '#166534' }}>
                      {currentBal < 0 ? '-' : ''}₹{Math.abs(currentBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </div>
                  </div>
                  {rechargeAmt !== 0 && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, color: rechargeAmt < 0 ? '#b91c1c' : '#047857', fontWeight: 600 }}>
                        {rechargeAmt < 0 ? 'Updated Balance After Deduction' : 'Updated Balance After Recharge'}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: updatedNewBal < 0 ? '#b91c1c' : '#065f46' }}>
                        {updatedNewBal < 0 ? '-' : ''}₹{Math.abs(updatedNewBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                </div>
              </Grid>
            )}

            {/* Amount Input */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={amount}
                onChange={handleChange('amount')}
                variant="outlined"
                required
                disabled={loading}
                inputProps={{ step: 'any' }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
            </Grid>

            {/* Payment Mode */}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Payment Mode"
                value={paymentMode}
                onChange={handleChange('paymentMode')}
                variant="outlined"
                required
                disabled={loading}
              >
                {PAYMENT_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Reference No */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Transaction / Reference No"
                value={referenceNo}
                onChange={handleChange('referenceNo')}
                variant="outlined"
                disabled={loading}
                placeholder="e.g. UTR / Txn ID / Cheque No"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes / Remarks"
                value={notes}
                onChange={handleChange('notes')}
                variant="outlined"
                disabled={loading}
                multiline
                rows={2}
                placeholder="Optional recharge notes"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions style={{ padding: '16px 24px', backgroundColor: '#ffffff', justifyContent: 'flex-end', gap: 12 }}>
          <Button onClick={onClose} disabled={loading} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            style={{
              backgroundColor: '#00a65a',
              color: '#ffffff',
              fontWeight: 600,
              padding: '8px 24px',
            }}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AccountBalanceWalletIcon />}
          >
            {loading ? 'Processing...' : (rechargeAmt < 0 ? 'Deduct / Adjust Wallet' : 'Recharge Wallet')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}


